// web/src/main.tsx — SPA entry. Composes the M0 enterprise app shell (persistent
// left nav + top bar + breadcrumb + routed outlet) with the M2 enrollment +
// sealing ceremony.
//
// The legacy single-approver console (the Vault grid bound to api.listSecrets,
// the My-Access reveal rail bound to api.revealGrant, the approval queue bound
// to the old decide model, plus CreateSecretModal) is RETIRED: those custodial
// endpoints were removed server-side when the zero-trust seal write-path
// (POST /records, ADR-003 D1/D2) replaced server-held material. main.tsx no
// longer references any removed endpoint or type.
//
// Shell-level state (principal, theme, act-as, realtime) is owned here and
// published to the routed pages via the Outlet context. A SINGLE WebSocket
// subscription fans the events that still exist (audit.appended + the request
// lifecycle, kept for forward-compat) into local state — no polling (R4.2).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import './styles/tokens.css';
import './styles/global.css';
import './styles/shell.css';
import './styles/console.css';
import './styles/enroll.css';
import './styles/ceremony.css';
import './styles/records.css';
import './styles/trust.css';

import type { AuditEntry, Principal, RealtimeEvent } from '../../shared/types';

import { api } from './api';
import {
  fetchPrincipal,
  getActAs,
  setActAs,
  STUB_PRINCIPAL,
  STUB_PRINCIPALS,
  type ActAsId,
} from './auth';
import { RealtimeClient } from './ws';
import { useAsync } from './hooks';

import { AppShell } from './shell/AppShell';
import type { ShellState } from './shell/ShellContext';
import { Console, type ConsoleData, type SealedRecordView } from './routes/Console';
import {
  ProofsPage,
  MyAccessPage,
  AuditPage,
  ResponsibilitiesPage,
  SettingsPage,
  NotFoundPage,
} from './routes/Pages';
import { PageLoading } from './components/ui';
import { NewSecretWizard, type SecretDraft } from './components/enroll/NewSecretWizard';
import { InitiatorFlow } from './components/enroll/InitiatorFlow';
import { ReceiverFlow } from './components/enroll/ReceiverFlow';
import { UnsealCeremony } from './components/ceremony/UnsealCeremony';
import { RecordDetailPage } from './routes/RecordDetail';
import {
  CommandPalette,
  useCommands,
  usePaletteHotkey,
} from './components/CommandPalette';

const THEME_KEY = 'bg-theme';

function useTheme(): [string, () => void] {
  const [theme, setTheme] = useState<string>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

const ENVIRONMENT: string =
  (import.meta.env.VITE_ENV as string | undefined) ??
  (import.meta.env.DEV ? 'development' : 'preview');

/** What the shell hands down to its routed pages via the Outlet context. */
interface OutletData extends ConsoleData {
  /** Opens the seal wizard from anywhere behind the shell. */
  openWizard: () => void;
}

function useShellData(): OutletData {
  return useOutletContext<OutletData>();
}

/** Initiator route: needs the resolved principal; the draft rides in router state. */
function EnrollRoute(): React.JSX.Element {
  const { principal } = useShellData();
  return <InitiatorFlow principal={principal} />;
}

/** Unseal ceremony (M3): the animated safe. Ceremony context rides in router state. */
function UnsealRoute(): React.JSX.Element {
  const { principal } = useShellData();
  return <UnsealCeremony principal={principal} />;
}

function App(): React.JSX.Element {
  const [theme, toggleTheme] = useTheme();
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  // Act-as identity (D-013). Switching re-fetches /me + reconnects the WS.
  const [actAs, setActAsState] = useState<ActAsId>(() => getActAs());

  // Audit timeline — one-shot load, kept fresh by realtime thereafter.
  const auditState = useAsync<AuditEntry[]>(api.listAudit);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  useEffect(() => {
    if (auditState.data) setAudit(auditState.data);
  }, [auditState.data]);

  // Records sealed during this session. There is no records LIST endpoint yet
  // (lands in M4); InitiatorFlow seals via POST /records and shows its own
  // receipt, so this stays empty until that read-path arrives.
  const [sealedRecords] = useState<SealedRecordView[]>([]);

  const clientRef = useRef<RealtimeClient | null>(null);

  // Resolve the principal (GET /me); fall back to the act-as stub if offline.
  useEffect(() => {
    let cancelled = false;
    fetchPrincipal()
      .then((p) => !cancelled && setPrincipal(p))
      .catch(() => !cancelled && setPrincipal(STUB_PRINCIPALS[actAs] ?? STUB_PRINCIPAL));
    return () => {
      cancelled = true;
    };
  }, [actAs]);

  // Switch identity: persist, re-fetch /me, and reconnect the WS so its
  // handshake carries the new ?actAs identity.
  const switchActAs = useCallback((id: ActAsId) => {
    if (id === getActAs()) return;
    setActAs(id);
    setActAsState(id);
    clientRef.current?.reconnect();
  }, []);

  // Single WS subscription — fans the surviving RealtimeEvents into state. No
  // polling. The request.* lifecycle events are kept for forward-compat with the
  // quorum inbox (M3); today only audit.appended drives a visible surface.
  useEffect(() => {
    const client = new RealtimeClient();
    clientRef.current = client;

    const off = client.on((ev: RealtimeEvent) => {
      switch (ev.type) {
        case 'hello':
          setWsConnected(true);
          break;
        case 'audit.appended':
          setAudit((cur) => (cur.some((e) => e.id === ev.entry.id) ? cur : [...cur, ev.entry]));
          break;
      }
    });

    client.connect();
    return () => {
      off();
      client.close();
      setWsConnected(false);
    };
  }, []);

  const verify = useCallback(() => api.verifyAudit(), []);

  if (!principal) {
    return <PageLoading message="Authenticating…" />;
  }

  const shell: ShellState = {
    principal,
    theme,
    toggleTheme,
    actAs,
    switchActAs,
    wsConnected,
    environment: ENVIRONMENT,
  };

  return (
    <Routes>
      <Route
        element={
          <ShellOutlet
            shell={shell}
            principal={principal}
            audit={audit}
            sealedRecords={sealedRecords}
            onVerify={verify}
          />
        }
      >
        <Route index element={<Console />} />
        <Route path="records/:id" element={<RecordDetailPage />} />
        <Route path="proofs" element={<ProofsPage />} />
        <Route path="my-access" element={<MyAccessPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="responsibilities" element={<ResponsibilitiesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="enroll" element={<EnrollRoute />} />
        <Route path="unseal/:id" element={<UnsealRoute />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* The receiver join flow is a standalone, shell-less surface (it may be
          reached by an external guest via a magic link). */}
      <Route path="/join" element={<ReceiverFlow />} />
      <Route path="/join/:code" element={<ReceiverFlow />} />
    </Routes>
  );
}

/** The shell + its routed outlet. Owns the seal-wizard modal (openable anywhere). */
function ShellOutlet({
  shell,
  principal,
  audit,
  sealedRecords,
  onVerify,
}: {
  shell: ShellState;
  principal: Principal;
  audit: AuditEntry[];
  sealedRecords: SealedRecordView[];
  onVerify: ConsoleData['onVerify'];
}): React.JSX.Element {
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // On "Convene & seal" the wizard hands its in-memory draft to the initiator
  // lobby via router location state (R11: plaintext is never persisted or sent
  // except as ciphertext). InitiatorFlow opens the session, gathers admitted
  // members' public keys, splits + encrypts client-side, and POSTs /records.
  const convene = useCallback(
    async (draft: SecretDraft) => {
      setWizardOpen(false);
      navigate('/enroll', { state: { draft } });
    },
    [navigate],
  );

  // ⌘K / Ctrl-K (and "/") open the command palette from anywhere behind the shell.
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  usePaletteHotkey(openPalette);

  // Switching identity from the palette flips between the two stub principals.
  const switchToOther = useCallback(() => {
    shell.switchActAs(shell.actAs === 'rhys' ? 'dana' : 'rhys');
  }, [shell]);

  const commands = useCommands({
    onNewSecret: () => setWizardOpen(true),
    onToggleTheme: shell.toggleTheme,
    theme: shell.theme,
    onSwitchIdentity: switchToOther,
  });

  const data: OutletData = {
    principal,
    audit,
    sealedRecords,
    onVerify,
    onNewSecret: () => setWizardOpen(true),
    openWizard: () => setWizardOpen(true),
  };

  const shellWithPalette: ShellState = { ...shell, openPalette };

  return (
    <>
      <AppShell shell={shellWithPalette} outletContext={data} />
      {wizardOpen && (
        <NewSecretWizard onConvene={convene} onClose={() => setWizardOpen(false)} />
      )}
      {paletteOpen && (
        <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} />
      )}
    </>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');
createRoot(rootEl).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
);
